import React, { useEffect, useState, useRef } from 'react';
import JSZip from 'jszip';
import { X, Download, Loader, Eye, Printer } from 'lucide-react';
import { generateProjectHtmlReportContentWithPaths } from '../reportGenerator';
import { downloadFile } from '../utils';
import type { Project, User, Item } from '../types/core';

interface ExportProgressModalProps {
  open: boolean;
  onClose: () => void;
  project: Project;
  currentUser: User;
  availablePurchasers: User[];
  t: (key: string, options?: any) => string;
}

const ExportProgressModal: React.FC<ExportProgressModalProps> = ({ open, onClose, project, currentUser, availablePurchasers, t }) => {
  const [progress, setProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [zipBlob, setZipBlob] = useState<Blob | null>(null);
  const [zipFileName, setZipFileName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewHtmlContent, setPreviewHtmlContent] = useState<string | null>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (open) {
      setProgress(0);
      setIsExporting(false);
      setIsReady(false);
      setZipBlob(null);
      setZipFileName('');
      setError(null);
      setPreviewHtmlContent(null);
      hasStarted.current = false;
      hasStarted.current = true;
      startExport();
    }
    if (!open) {
      setProgress(0);
      setIsExporting(false);
      setIsReady(false);
      setZipBlob(null);
      setZipFileName('');
      setError(null);
      setPreviewHtmlContent(null);
      hasStarted.current = false;
    }
  }, [open]);

  const startExport = async () => {
    setIsExporting(true);
    setError(null);
    setPreviewHtmlContent(null);

    try {
      const isUserExport = currentUser.role === 'user';
      let itemsToExport = project.items.filter((item: Item) => {
        if (item.isGroup) {
          const itemIdStr = String(item.id);
          return project.items.some(child => String(child.parentId) === itemIdStr && (child.status === 'approved' || child.status === 'bought' || child.isUnsupervisedPurchase === true));
        } else {
          return item.status === 'approved' || item.status === 'bought' || item.isUnsupervisedPurchase === true;
        }
      });
      let projectNameForExport = project.name;
      if (isUserExport) {
        itemsToExport = itemsToExport.filter((item: Item) => {
          if (item.purchaser === currentUser.id) return true;
          if (item.isGroup) {
            const itemIdStr = String(item.id);
            return project.items.some(child => String(child.parentId) === itemIdStr && child.purchaser === currentUser.id);
          }
          return false;
        });
        projectNameForExport = `${project.name}_${currentUser.fullName}`;
      }

      setProgress(5);
      const reportCreationDate = new Date().toLocaleString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
      const htmlReportContentForZip = generateProjectHtmlReportContentWithPaths(
        project,
        projectNameForExport,
        itemsToExport,
        availablePurchasers,
        reportCreationDate,
        t,
        true // Use local paths for ZIP export
      );

      setProgress(10);

      setProgress(15);
      const zip = new JSZip();
      const attachmentsFolder = zip.folder('attachments');
      const pdfsFolder = zip.folder('PDFs'); // New folder for PDFs only
      const reportsFolder = zip.folder('reports');
      const purchaserFolders: Record<string, JSZip> = {};
      const pdfSubfolders: Record<string, JSZip> = {};

      reportsFolder!.file('project_summary.html', htmlReportContentForZip);

      // Calculate total attachments including child items for groups
      let totalAttachmentsInZip = 0;
      itemsToExport.forEach(item => {
        totalAttachmentsInZip += item.attachments?.length || 0;
        // If this is a group, add attachments from its children
        if (item.isGroup) {
          const itemIdStr = String(item.id);
          const children = project.items.filter(child => String(child.parentId) === itemIdStr);
          children.forEach(child => {
            totalAttachmentsInZip += child.attachments?.length || 0;
          });
        }
      });
      let processedAttachments = 0;

      for (const item of itemsToExport) {
        let effectivePurchaserId = item.purchaser;

        // If no direct purchaser and it's a group, try to infer from children (to match report logic)
        if (!effectivePurchaserId && item.isGroup && project.items) {
          const itemIdStr = String(item.id);
          const children = project.items.filter((child: Item) => String(child.parentId) === itemIdStr);
          const childWithPurchaser = children.find((c: Item) => c.purchaser);
          if (childWithPurchaser) {
            effectivePurchaserId = childWithPurchaser.purchaser;
          }
        }

        const purchaser = isUserExport ? currentUser : availablePurchasers.find(p => p.id === effectivePurchaserId);
        const purchaserName = purchaser?.fullName || 'Unassigned';
        const sanitizedPurchaserName = purchaserName.replace(/[^-\u007F\p{L}\p{M}\p{N}\s-]/gu, '_').replace(/\s+/g, '_');
        if (!purchaserFolders[sanitizedPurchaserName]) {
          purchaserFolders[sanitizedPurchaserName] = attachmentsFolder!.folder(sanitizedPurchaserName) as JSZip;
        }
        const sanitizedItemFolderName = item.name.replace(/[^-\u007F\p{L}\p{M}\p{N}\s-]/gu, '_').replace(/\s+/g, '_');
        const itemFolder = purchaserFolders[sanitizedPurchaserName].folder(sanitizedItemFolderName) as JSZip;

        // Process attachments for the main item (group or individual)
        if (item.attachments && item.attachments.length > 0) {
          for (const [index, att] of item.attachments.entries()) {
            try {
              const sanitizedAttachmentFileName = `${index + 1}_${att.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
              if (att.url) {
                try {
                  // Check if this is a PDF - save to separate PDFs folder
                  if (att.type === 'application/pdf') {

                    // Create subfolder structure in PDFs folder: PDFs/PurchaserName/ItemName/
                    const pdfPurchaserKey = `${sanitizedPurchaserName}`;
                    if (!pdfSubfolders[pdfPurchaserKey]) {
                      pdfSubfolders[pdfPurchaserKey] = pdfsFolder!.folder(sanitizedPurchaserName) as JSZip;
                    }
                    const pdfItemFolder = pdfSubfolders[pdfPurchaserKey].folder(sanitizedItemFolderName) as JSZip;

                    try {
                      const response = await fetch(att.url);
                      if (!response.ok) throw new Error(`Failed to fetch ${att.name}: ${response.status} ${response.statusText}`);
                      const fileBlob = await response.blob();
                      pdfItemFolder.file(sanitizedAttachmentFileName, fileBlob);
                    } catch (pdfError: any) {
                      pdfItemFolder.file(`ERROR_FETCHING_${sanitizedAttachmentFileName}.txt`, `Error fetching PDF: ${att.name}\nURL: ${att.url}\nError: ${pdfError.message || pdfError}`);
                    }
                  } else {
                    // Handle non-PDF files normally
                    const response = await fetch(att.url);
                    if (!response.ok) throw new Error(`Failed to fetch ${att.name}: ${response.status} ${response.statusText}`);
                    const fileBlob = await response.blob();
                    itemFolder.file(sanitizedAttachmentFileName, fileBlob);
                  }
                } catch (fetchError: any) {
                  itemFolder.file(`ERROR_FETCHING_${sanitizedAttachmentFileName}.txt`, `Error fetching attachment: ${att.name}\nURL: ${att.url}\nError: ${fetchError.message || fetchError}`);
                }
              } else if (att.data && (att as any).isLegacy) {
                const base64Data = att.data.split(',')[1];
                if (base64Data) {
                  const binaryString = atob(base64Data);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) { bytes[i] = binaryString.charCodeAt(i); }
                  itemFolder.file(sanitizedAttachmentFileName, bytes);
                } else {
                  itemFolder.file(`ERROR_PROCESSING_LEGACY_${sanitizedAttachmentFileName}.txt`, `Error processing legacy attachment: ${att.name}\nInvalid base64 data.`);
                }
              } else {
                itemFolder.file(`UNKNOWN_ATTACHMENT_${sanitizedAttachmentFileName}.txt`, `Attachment type not recognized or data missing for ${att.name}.`);
              }
            } catch (error: any) {
              const errorFileNameBase = att.name.replace(/[^a-zA-Z0-9.-]/g, '_');
              itemFolder.file(`ERROR_PROCESSING_${index + 1}_${errorFileNameBase}.txt`, `Error processing attachment: ${att.name}\nError: ${error.message || error}`);
            }
            processedAttachments++;
            setProgress(Math.round((processedAttachments / (totalAttachmentsInZip + 2)) * 100));
          }
        }

        // If this is a group, also process attachments from child items
        if (item.isGroup) {
          const groupIdStr = String(item.id);
          const children = project.items.filter(child => String(child.parentId) === groupIdStr);
          for (const child of children) {
            if (child.attachments && child.attachments.length > 0) {
              // Create a subfolder for the child item within the group folder
              const sanitizedChildFolderName = `${child.name.replace(/[^-\u007F\p{L}\p{M}\p{N}\s-]/gu, '_').replace(/\s+/g, '_')}_child`;
              const childFolder = itemFolder.folder(sanitizedChildFolderName) as JSZip;

              for (const [index, att] of child.attachments.entries()) {
                try {
                  const sanitizedAttachmentFileName = `${index + 1}_${att.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
                  if (att.url) {
                    try {
                      // Check if this is a PDF - save to separate PDFs folder
                      if (att.type === 'application/pdf') {

                        // Create subfolder structure in PDFs folder: PDFs/PurchaserName/ItemName_child/
                        const pdfPurchaserKey = `${sanitizedPurchaserName}`;
                        if (!pdfSubfolders[pdfPurchaserKey]) {
                          pdfSubfolders[pdfPurchaserKey] = pdfsFolder!.folder(sanitizedPurchaserName) as JSZip;
                        }
                        const pdfChildFolder = pdfSubfolders[pdfPurchaserKey].folder(sanitizedChildFolderName) as JSZip;

                        try {
                          const response = await fetch(att.url);
                          if (!response.ok) throw new Error(`Failed to fetch ${att.name}: ${response.status} ${response.statusText}`);
                          const fileBlob = await response.blob();
                          pdfChildFolder.file(sanitizedAttachmentFileName, fileBlob);
                        } catch (pdfError: any) {
                          pdfChildFolder.file(`ERROR_FETCHING_${sanitizedAttachmentFileName}.txt`, `Error fetching PDF: ${att.name}\nURL: ${att.url}\nError: ${pdfError.message || pdfError}`);
                        }
                      } else {
                        // Handle non-PDF files normally
                        const response = await fetch(att.url);
                        if (!response.ok) throw new Error(`Failed to fetch ${att.name}: ${response.status} ${response.statusText}`);
                        const fileBlob = await response.blob();
                        childFolder.file(sanitizedAttachmentFileName, fileBlob);
                      }
                    } catch (fetchError: any) {
                      childFolder.file(`ERROR_FETCHING_${sanitizedAttachmentFileName}.txt`, `Error fetching attachment: ${att.name}\nURL: ${att.url}\nError: ${fetchError.message || fetchError}`);
                    }
                  } else if (att.data && (att as any).isLegacy) {
                    const base64Data = att.data.split(',')[1];
                    if (base64Data) {
                      const binaryString = atob(base64Data);
                      const bytes = new Uint8Array(binaryString.length);
                      for (let i = 0; i < binaryString.length; i++) { bytes[i] = binaryString.charCodeAt(i); }
                      childFolder.file(sanitizedAttachmentFileName, bytes);
                    } else {
                      childFolder.file(`ERROR_PROCESSING_LEGACY_${sanitizedAttachmentFileName}.txt`, `Error processing legacy attachment: ${att.name}\nInvalid base64 data.`);
                    }
                  } else {
                    childFolder.file(`UNKNOWN_ATTACHMENT_${sanitizedAttachmentFileName}.txt`, `Attachment type not recognized or data missing for ${att.name}.`);
                  }
                } catch (error: any) {
                  const errorFileNameBase = att.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                  childFolder.file(`ERROR_PROCESSING_${index + 1}_${errorFileNameBase}.txt`, `Error processing attachment: ${att.name}\nError: ${error.message || error}`);
                }
                processedAttachments++;
                setProgress(Math.round((processedAttachments / (totalAttachmentsInZip + 2)) * 100));
              }
            }
          }
        }
      }

      if (attachmentsFolder) {
        const fundAttachmentRoot = attachmentsFolder.folder('fund_attachment');
        if (fundAttachmentRoot) {
          for (const purchaserFund of project.purchaserFunds) {
            const userId = purchaserFund.purchaserId;
            const userFolder = fundAttachmentRoot.folder(userId);
            if (userFolder) {
              for (const fundEntry of purchaserFund.fundEntries) {
                if (fundEntry.attachments && fundEntry.attachments.length > 0) {
                  const fundEntryFolder = userFolder.folder(fundEntry.id.toString());
                  if (fundEntryFolder) {
                    for (const [index, att] of fundEntry.attachments.entries()) {
                      try {
                        const sanitizedAttachmentFileName = `${index + 1}_${att.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
                        if (att.url) {
                          try {
                            // Check if this is a PDF - save to separate PDFs folder
                            if (att.type === 'application/pdf') {

                              // Create subfolder structure in PDFs folder: PDFs/FundDocuments/UserID/
                              const fundPdfFolder = pdfsFolder!.folder('FundDocuments') as JSZip;
                              const userFundPdfFolder = fundPdfFolder.folder(userId) as JSZip;
                              const fundEntryPdfFolder = userFundPdfFolder.folder(`FundEntry_${fundEntry.id}`) as JSZip;

                              try {
                                const response = await fetch(att.url);
                                if (!response.ok) throw new Error(`Failed to fetch ${att.name}: ${response.status} ${response.statusText}`);
                                const fileBlob = await response.blob();
                                fundEntryPdfFolder.file(sanitizedAttachmentFileName, fileBlob);
                              } catch (pdfError: any) {
                                fundEntryPdfFolder.file(`ERROR_FETCHING_${sanitizedAttachmentFileName}.txt`, `Error fetching PDF: ${att.name}\nURL: ${att.url}\nError: ${pdfError.message || pdfError}`);
                              }
                            } else {
                              // Handle non-PDF files normally
                              const response = await fetch(att.url);
                              if (!response.ok) throw new Error(`Failed to fetch ${att.name}: ${response.status} ${response.statusText}`);
                              const fileBlob = await response.blob();
                              fundEntryFolder.file(sanitizedAttachmentFileName, fileBlob);
                            }
                          } catch (fetchError: any) {
                            fundEntryFolder.file(`ERROR_FETCHING_${sanitizedAttachmentFileName}.txt`, `Error fetching attachment: ${att.name}\nURL: ${att.url}\nError: ${fetchError.message || fetchError}`);
                          }
                        } else if (att.data && (att as any).isLegacy) {
                          const base64Data = att.data.split(',')[1];
                          if (base64Data) {
                            const binaryString = atob(base64Data);
                            const bytes = new Uint8Array(binaryString.length);
                            for (let i = 0; i < binaryString.length; i++) { bytes[i] = binaryString.charCodeAt(i); }
                            fundEntryFolder.file(sanitizedAttachmentFileName, bytes);
                          } else {
                            fundEntryFolder.file(`ERROR_PROCESSING_LEGACY_${sanitizedAttachmentFileName}.txt`, `Error processing legacy attachment: ${att.name}\nInvalid base64 data.`);
                          }
                        } else {
                          fundEntryFolder.file(`UNKNOWN_ATTACHMENT_${sanitizedAttachmentFileName}.txt`, `Attachment type not recognized or data missing for ${att.name}.`);
                        }
                      } catch (error: any) {
                        const errorFileNameBase = att.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                        fundEntryFolder.file(`ERROR_PROCESSING_${index + 1}_${errorFileNameBase}.txt`, `Error processing attachment: ${att.name}\nError: ${error.message || error}`);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      setProgress(Math.round(((totalAttachmentsInZip + 1) / (totalAttachmentsInZip + 2)) * 100));
      const readmeContent = `# ${projectNameForExport} - Export

## Contents
- **reports/project_summary.html** - Main project report with all details
- **attachments/** - All project attachments organized by purchaser and item
- **attachments/fund_attachment/** - Financial documents and receipts

## Important Notes
- Open reports/project_summary.html to view the complete project report
- If you see "Image not available" messages in the report, check the attachments folder for error files
- Error files (ERROR_FETCHING_*.txt) contain details about files that couldn't be downloaded from storage
- This typically happens when files have been deleted from Firebase Storage but references still exist

## File Structure
The attachments are organized as follows:
\`\`\`
attachments/
├── [Purchaser Name]/
│   └── [Item Name]/
│       ├── [1_filename.ext, 2_filename.ext, ...] (group-level attachments)
│       └── [Child Item Name]_child/
│           └── [1_filename.ext, 2_filename.ext, ...] (child item attachments)
└── fund_attachment/
    └── [User ID]/
        └── [Fund Entry ID]/
            └── [1_filename.ext, 2_filename.ext, ...]
\`\`\`

Generated on: ${new Date().toLocaleString()}`;
      zip.file('README.md', readmeContent);

      setProgress(Math.round(((totalAttachmentsInZip + 2) / (totalAttachmentsInZip + 2)) * 100));
      const timestamp = Date.now().toString();
      const safeProjectName = projectNameForExport.replace(/[^-\u007F\p{L}\p{M}\p{N}-]/gu, '_').replace(/\s+/g, '_');
      const zipFileName = `${safeProjectName}_Export_${timestamp}.zip`;
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
        streamFiles: true,
      }, (metadata) => {
        const zipProgress = metadata.percent;
        const overallProgress = 99 + (zipProgress / 100);
        setProgress(Math.round(overallProgress));
      });

      setZipBlob(zipBlob);
      setZipFileName(zipFileName);
      setProgress(100);
      setIsReady(true);
      setIsExporting(false);

      // Generate preview HTML report
      const previewItemsToUse = project.items.filter((item: Item) => {
        if (item.isGroup) {
          const itemIdStr = String(item.id);
          return project.items.some(child => String(child.parentId) === itemIdStr && (child.status === 'approved' || child.status === 'bought' || child.isUnsupervisedPurchase === true));
        } else {
          return item.status === 'approved' || item.status === 'bought' || item.isUnsupervisedPurchase === true;
        }
      });
      const previewProjectNameForExport = isUserExport ? `${project.name}_${currentUser.fullName}` : project.name;
      const previewReportCreationDate = new Date().toLocaleString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

      try {
        // Use the same unified report generator as the export, but with external URLs (not local paths)
        const generatedPreviewHtml = generateProjectHtmlReportContentWithPaths(
          project,
          previewProjectNameForExport,
          previewItemsToUse,
          availablePurchasers,
          previewReportCreationDate,
          t
        );
        setPreviewHtmlContent(generatedPreviewHtml);
      } catch (previewError: any) {
        setError('Failed to generate report preview. ' + (previewError.message || ''));
      }

      setIsExporting(false);
      setProgress(100);

    } catch (err: any) {
      setError(err.message || 'Export failed');
      setIsExporting(false);
      setProgress(0);
      setIsReady(false);
    }
  };

  const handleDownload = () => {
    if (zipBlob && zipFileName) {
      downloadFile(zipBlob, zipFileName);
      onClose();
    }
  };

  const handlePreview = () => {
    if (previewHtmlContent) {
      const previewWindow = window.open('', '_blank');
      if (previewWindow) {
        previewWindow.document.write(previewHtmlContent);
        previewWindow.document.close();
      } else {
        alert('Please allow pop-ups to preview the report.');
      }
    }
  };

  const handlePrint = () => {
    if (previewHtmlContent) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(previewHtmlContent);
        printWindow.document.close();

        // Print only after every image has loaded (or failed). The old 1s timer
        // fired before the Firebase Storage images arrived, so the proof-of-payment
        // and product-picture pages came out blank in the printed PDF.
        const pending = Array.from(printWindow.document.images)
          .filter(img => !img.complete)
          .map(img => new Promise<void>(resolve => {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          }));
        // ponytail: 60s cap so one dead URL cannot block printing forever
        const cap = new Promise<void>(resolve => setTimeout(resolve, 60000));
        Promise.race([Promise.all(pending), cap]).then(() => {
          printWindow.focus();
          printWindow.print();
        });
      } else {
        alert('Please allow pop-ups to print the report.');
      }
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{t('project.export')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <div className="mb-2 text-sm text-gray-700">
              {error ? (
                t('export.error', { defaultValue: 'An error occurred during export.' })
              ) : isExporting ? (
                t('export.description', { defaultValue: 'Preparing your export file. Please wait...' })
              ) : isReady && previewHtmlContent ? (
                t('export.complete.preview.ready', { defaultValue: 'Export complete. Preview is ready.' })
              ) : isReady ? (
                t('export.complete.no.preview', { defaultValue: 'Export complete. Download is ready.' })
              ) : (
                t('export.description', { defaultValue: 'Preparing your export file. Please wait...' })
              )}
            </div>
            {!error && (
              <div className="export-progress-bar-bg mb-4">
                <div
                  className="export-progress-bar-fg"
                  style={{ width: `${progress}%` }}
                >
                </div>
                <span className="export-progress-bar-text">{progress}%</span>
              </div>
            )}
            {error && <div className="mt-2 text-red-600 text-sm">{error}</div>}
          </div>
          {
            isReady && !isExporting && (
              <div className="flex justify-end space-x-3">
                {previewHtmlContent && (
                  <>
                    <button
                      className="btn-secondary px-4 py-2 rounded font-semibold transition-all flex items-center"
                      onClick={handlePreview}
                    >
                      <Eye className="w-5 h-5 mr-2 inline-block" />
                      {t('action.preview.report', { defaultValue: 'Preview Report' })}
                    </button>
                    <button
                      className="btn-secondary px-4 py-2 rounded font-semibold transition-all flex items-center"
                      onClick={handlePrint}
                    >
                      <Printer className="w-5 h-5 mr-2 inline-block" />
                      {t('action.print.report', { defaultValue: 'Print Report' })}
                    </button>
                  </>
                )}
                <button
                  className="btn-primary px-4 py-2 rounded font-semibold transition-all flex items-center"
                  onClick={handleDownload}
                >
                  <Download className="w-5 h-5 mr-2 inline-block" />
                  {t('action.download', { defaultValue: 'Download' })}
                </button>
              </div>
            )
          }
          {
            isExporting && !error && (
              <div className="text-center text-sm text-gray-600 mt-4 flex items-center justify-center">
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                {previewHtmlContent ? t('export.generating.preview', { defaultValue: 'Generating report preview...' }) : t('export.generating.zip', { defaultValue: 'Generating zip file...' })}
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
};

export default ExportProgressModal; 